import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Section, Toolbar } from '@vnetwork/ui/layout';
import { PieChart } from '@vnetwork/ui/charts';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  MultiSelect,
  type MultiSelectOption,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuCheckboxItem,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { SlidersHorizontal } from 'lucide-react';
import { CentralMetric, resolveTimeRange, type TimeRange } from '@/api/manticore';
import { useServiceCdnDomainsPaged } from '@/api/manticore/service-cdn';
import { useOptionalCards } from '../optional-cards-prefs';
import { TopPathsCard } from './overview-cards/TopPathsCard';
import { TopDomainsCard } from './overview-cards/TopDomainsCard';
import styles from './ServiceOverviewTab.module.css';
import { toneColor } from '../../shared';
import {
  RangeControl,
  RefreshButton,
  KpiTiles,
  ChartCard,
  BarCard,
  Stack,
  CardGrid,
  type Kpi,
} from '../../_shared/AnalyticsKit';
import {
  formatBps,
  formatBytes,
  formatBytesAxis,
  formatNum,
  formatCount,
  formatPct,
  sumSeries,
  maxSeries,
  avgSeries,
  peakRateDayCaveat,
  PERIOD_LABEL,
} from '../../_shared/analytics-format';
import { useScopedSeries, mergeRows, seriesRows, mapToBars, STATUS_GROUPS, SERVICE_PERIODS } from '../analytics-series';

/** Service overview view — aggregate traffic, cache efficiency, response health. */
export function ServiceOverviewTab({ projectIds }: { projectIds?: string }) {
  const { serviceId } = useParams<{ serviceId?: string }>();
  const [range, setRange] = useState<TimeRange>({ kind: 'preset', period: '24h' });
  const [domainIds, setDomainIds] = useState<string[]>([]);
  // Reset the domain scope when switching service — the previous service's domain
  // ids are meaningless for the new one.
  useEffect(() => setDomainIds([]), [serviceId]);
  const qc = useQueryClient();
  const resolved = useMemo(() => resolveTimeRange(range), [range]);
  const rangeLabel = range.kind === 'preset' ? PERIOD_LABEL[range.period] : 'the selected range';

  // Opt-in cards (Top URLs / Top domains) — default hidden, choice persisted in
  // localStorage. Hidden cards issue no request. Custom ranges send 'custom' so
  // the live top-paths pull uses from/to instead of a preset token.
  const { cards, toggle } = useOptionalCards();
  const livePeriod = range.kind === 'preset' ? range.period : 'custom';

  // Domain picker — service-scoped only. Options are the service family's domains;
  // an empty selection means "all" (no narrowing).
  const domainsQ = useServiceCdnDomainsPaged(serviceId ?? '', {
    page: 1,
    perPage: 200,
    enabled: !!serviceId,
  });
  const domainOptions = useMemo<MultiSelectOption[]>(
    () =>
      (domainsQ.data?.data ?? [])
        .filter((d) => d.id)
        .map((d) => ({ value: d.id as string, label: d.domain })),
    [domainsQ.data],
  );

  const common = {
    domainId: domainIds.length ? domainIds.join(',') : 'all',
    projectIds,
    period: range.kind === 'preset' ? range.period : '24h',
    range: range.kind === 'custom' ? resolved : undefined,
  } as const;

  const reqQ = useScopedSeries({ ...common, metric: CentralMetric.Requests });
  const volQ = useScopedSeries({ ...common, metric: CentralMetric.Bandwidth });
  const rateQ = useScopedSeries({ ...common, metric: CentralMetric.BandwidthRate });
  const hitRatioQ = useScopedSeries({ ...common, metric: CentralMetric.ReqHitRatio });
  const hitsQ = useScopedSeries({ ...common, metric: CentralMetric.CacheHits });
  const missQ = useScopedSeries({ ...common, metric: CentralMetric.CacheMisses });
  const statusQ = useScopedSeries({ ...common, metric: CentralMetric.Status, breakdown: 'code' });
  const provQ = useScopedSeries({ ...common, metric: CentralMetric.Requests, groupBy: 'provider' });

  const loading = reqQ.isLoading || volQ.isLoading || rateQ.isLoading;

  // Day-tier peak is a sum-of-daily-peaks across domains (see peakRateDayCaveat);
  // surface that on the peak KPI + bandwidth chart so a high monthly value reads
  // as expected, not as a data glitch.
  const peakCaveat = peakRateDayCaveat(rateQ.data?.granularity);

  const kpis: Kpi[] = [
    {
      label: 'Peak bandwidth',
      value: formatBps(maxSeries(rateQ.data?.series)),
      // Default: explain it's the highest instantaneous rate. Day-tier: the
      // sum-of-daily-peaks caveat replaces it (why a monthly peak reads higher).
      hint: peakCaveat || `Highest edge bandwidth rate reached over ${rangeLabel}.`,
    },
    {
      label: 'Volume',
      value: formatBytes(sumSeries(volQ.data?.series)),
      hint: `Total bytes served from the edge over ${rangeLabel}.`,
    },
    {
      label: 'Total requests',
      value: formatNum(sumSeries(reqQ.data?.series)),
      hint: `Total edge requests served over ${rangeLabel}.`,
    },
    {
      label: 'Cache hit ratio',
      value: formatPct(avgSeries(hitRatioQ.data?.series), '0..1'),
      hint: 'Share of requests served from edge cache — higher means less origin load.',
      dotColor: toneColor('success'),
    },
  ];

  const statusPie = useMemo(
    () =>
      STATUS_GROUPS.map((g) => ({
        name: g.label,
        value: sumSeries(statusQ.data?.by_dimension?.[g.key]),
        color: toneColor(g.tone),
      })).filter((s) => s.value > 0),
    [statusQ.data],
  );

  const cache = useMemo(() => {
    const hits = sumSeries(hitsQ.data?.series);
    const miss = sumSeries(missQ.data?.series);
    return [
      { name: 'HIT', value: hits, color: toneColor('success') },
      { name: 'MISS', value: miss, color: toneColor('warning') },
    ].filter((s) => s.value > 0);
  }, [hitsQ.data, missQ.data]);

  const providerBars = useMemo(
    () => mapToBars(provQ.data?.by_provider, { max: 6 }),
    [provQ.data],
  );

  return (
    <Section>
      {/* Controls — two rows so the wide RangeControl SegmentedControl gets a full-width row
          on mobile and doesn't clip. Section.actions uses flex-shrink:0, so Toolbar lives
          in the body where it can flex-wrap freely. */}
      <Toolbar
        start={
          serviceId && domainOptions.length > 0 ? (
            <MultiSelect
              placeholder="All domains"
              options={domainOptions}
              value={domainIds}
              onChange={setDomainIds}
              className={styles.domainSelect}
            />
          ) : undefined
        }
        end={
          <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" size="md" startIcon={<SlidersHorizontal size={14} />}>
                  Display options
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Optional cards</DropdownMenuLabel>
                <DropdownMenuCheckboxItem
                  checked={cards.topPaths}
                  onCheckedChange={(v) => toggle('topPaths', !!v)}
                  onSelect={(e) => e.preventDefault()}
                >
                  Top URLs
                </DropdownMenuCheckboxItem>
                <DropdownMenuCheckboxItem
                  checked={cards.topDomains}
                  onCheckedChange={(v) => toggle('topDomains', !!v)}
                  onSelect={(e) => e.preventDefault()}
                >
                  Top domains
                </DropdownMenuCheckboxItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <RefreshButton onRefresh={() => qc.invalidateQueries({ queryKey: ['cdn', 'central-analytics'] })} />
          </div>
        }
      />
      {/* Range row — standalone so the SegmentedControl gets full width to avoid clip. */}
      <div style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center', flexWrap: 'wrap', minWidth: 0, marginBottom: 'var(--vn-space-3)' }}>
        <RangeControl options={SERVICE_PERIODS} value={range} onChange={setRange} />
      </div>
      <Stack>
        <KpiTiles items={kpis} loading={loading} />

        <ChartCard
          title="Traffic"
          description="Requests vs edge egress over the selected window."
          data={mergeRows(reqQ.data, volQ.data, 'req', 'vol')}
          xKey="t"
          series={[
            { key: 'req', label: 'Requests', color: toneColor('success') },
            { key: 'vol', label: 'Volume', color: toneColor('info') },
          ]}
          valueFormatter={(v, key) =>
            key === 'req'
              ? formatCount(Number(v)) // requests tooltip
              : key === 'vol'
                ? formatBytes(Number(v)) // volume tooltip (precise)
                : formatBytesAxis(Number(v)) // Y-axis ticks (no key) — short bytes
          }
          loading={reqQ.isLoading || volQ.isLoading}
        />

        <ChartCard
          title="Bandwidth"
          description={
            'Edge bandwidth rate over the selected window — peak indicates burst capacity used.' +
            (peakCaveat ? ` ${peakCaveat}` : '')
          }
          data={seriesRows(rateQ.data, 'bw')}
          xKey="t"
          series={[{ key: 'bw', label: 'Bandwidth', color: toneColor('info') }]}
          valueFormatter={(v) => formatBps(Number(v))}
          loading={rateQ.isLoading}
        />

        <CardGrid min={300}>
          <Card flat>
            <CardHeader>
              <CardTitle>HTTP status codes</CardTitle>
              <CardDescription>Response status mix across all requests in the window.</CardDescription>
            </CardHeader>
            <CardBody>
              {statusPie.length ? (
                <PieChart data={statusPie} height={200} donut showLegend valueFormatter={(v) => formatNum(Number(v))} />
              ) : (
                <EmptyState size="sm" title="No data" description="No data for this period." />
              )}
            </CardBody>
          </Card>
          <Card flat>
            <CardHeader>
              <CardTitle>Cache distribution</CardTitle>
              <CardDescription>Cache HIT vs MISS share served at the edge.</CardDescription>
            </CardHeader>
            <CardBody>
              {cache.length ? (
                <PieChart data={cache} height={200} donut showLegend valueFormatter={(v) => formatNum(Number(v))} />
              ) : (
                <EmptyState size="sm" title="No data" description="No data for this period." />
              )}
            </CardBody>
          </Card>
          <BarCard
            title="Traffic by provider"
            description="Request share served by each CDN provider."
            items={providerBars}
            loading={provQ.isLoading}
          />
        </CardGrid>

        {(cards.topPaths || cards.topDomains) && (
          <CardGrid min={360}>
            {cards.topPaths && (
              <TopPathsCard
                serviceId={serviceId}
                domainOptions={domainOptions}
                period={livePeriod}
                from={resolved.from}
                to={resolved.to}
              />
            )}
            {cards.topDomains && <TopDomainsCard domainOptions={domainOptions} from={resolved.from} to={resolved.to} />}
          </CardGrid>
        )}
      </Stack>
    </Section>
  );
}
